import { banco } from '../db/banco'
import type { ItemPedido, Meta, Pedido, Produto, SubPedido } from '../db/tipos'
import { arredondar2 } from '../pedidos/precos'
import { valorEfetivo } from '../comissoes/motor'
import { dentroDoPeriodo } from './calculo'
import { STATUS_VENDA } from './statusVenda'


export type NumerosDoMes = Record<Meta['tipo'], number> & { temVenda: boolean }

// UMA passada por mes devolve os seis numeros. realizadoDaMeta (metasDados.ts:34)
// faz uma varredura POR TIPO, o que serve para o Painel -- uma meta por vez --
// e nao serve para o criador: 6 tipos x 6 meses dariam 36 varreduras para
// montar uma tela, e no celular isso trava.
//
// realizadoDaMeta CONTINUA existindo e nao foi alterada. As duas sao comparadas
// num teste de equivalencia: ser mais rapida so vale se der o mesmo numero.
export async function numerosDoMes(
  inicio: string,
  fim: string,
  escopo: string,
): Promise<NumerosDoMes> {
  const representadaId = escopo.startsWith('representada:')
    ? escopo.slice('representada:'.length)
    : null

  const pedidos = await banco.pedidos
    .filter(
      (p) =>
        p.apagado_em === null &&
        p.status === 'fechado' &&
        dentroDoPeriodo(p.data, inicio, fim),
    )
    .toArray()

  const vendas: Array<{ subPedido: SubPedido; pedido: Pedido }> = []
  for (const pedido of pedidos) {
    const subs = await banco.sub_pedidos
      .where('pedido_id')
      .equals(pedido.id)
      .filter(
        (s) =>
          s.apagado_em === null &&
          STATUS_VENDA.includes(s.status) &&
          (representadaId === null || s.representada_id === representadaId),
      )
      .toArray()
    for (const subPedido of subs) vendas.push({ subPedido, pedido })
  }

  const vazio: NumerosDoMes = {
    valor_vendido: 0,
    faturamento_total: 0,
    positivacao: 0,
    itens_por_pedido: 0,
    cobertura_linhas: 0,
    comissao_necessaria: 0,
    temVenda: false,
  }
  if (vendas.length === 0) return vazio

  // Uma leitura de itens para TODAS as vendas do mes, e uma de produtos para os
  // ids que apareceram. E aqui que as 36 varreduras viram 1.
  const itens: ItemPedido[] = []
  for (const venda of vendas) {
    const doSub = await banco.itens_pedido
      .where('sub_pedido_id')
      .equals(venda.subPedido.id)
      .filter((i) => i.apagado_em === null)
      .toArray()
    itens.push(...doSub)
  }
  const produtos = new Map<string, Produto>()
  for (const id of new Set(itens.map((i) => i.produto_id))) {
    const produto = await banco.produtos.get(id)
    if (produto) produtos.set(id, produto)
  }

  const total = arredondar2(vendas.reduce((soma, v) => soma + v.subPedido.valor_total, 0))
  const idsPedidos = new Set(vendas.map((v) => v.pedido.id))

  let comissao = 0
  for (const venda of vendas) {
    const lancamento = await banco.lancamentos_comissao
      .where('sub_pedido_id')
      .equals(venda.subPedido.id)
      .filter((l) => l.apagado_em === null)
      .first()
    if (lancamento) comissao += valorEfetivo(lancamento)
  }

  // Cobertura so existe por representada: "linha" e sempre linha de uma marca.
  // No escopo pessoal fica 0, igual a metasDados.ts:63.
  let cobertura = 0
  if (representadaId) {
    const daRepresentada = await banco.produtos
      .where('representada_id')
      .equals(representadaId)
      .filter((p) => p.apagado_em === null && p.ativo && p.linha !== null)
      .toArray()
    const linhasExistentes = new Set(daRepresentada.map((p) => p.linha))
    if (linhasExistentes.size > 0) {
      const vendidas = new Set<string>()
      for (const item of itens) {
        const linha = produtos.get(item.produto_id)?.linha
        if (linha && linhasExistentes.has(linha)) vendidas.add(linha)
      }
      cobertura = arredondar2((vendidas.size / linhasExistentes.size) * 100)
    }
  }

  return {
    valor_vendido: total,
    faturamento_total: total,
    positivacao: new Set(vendas.map((v) => v.pedido.cliente_id)).size,
    itens_por_pedido: arredondar2(itens.length / idsPedidos.size),
    cobertura_linhas: cobertura,
    comissao_necessaria: arredondar2(comissao),
    temVenda: true,
  }
}
